import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { submitLead } from "../lib/api";
import { ArrowLeft, CheckCircle2, ArrowRight } from "lucide-react";

const INIT = {
    name: "",
    email: "",
    company: "",
    role: "",
    phone: "",
    preferred_date: "",
    message: "",
    interest: "demo",
};

export default function Demo() {
    const [form, setForm] = useState(INIT);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Name and email are required");
            return;
        }
        try {
            setBusy(true);
            await submitLead(form);
            setDone(true);
            toast.success("Demo request received. We'll be in touch within one business day.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not submit");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="demo-page" className="bg-[#0A0A0E] text-zinc-100 min-h-screen">
            <Navbar />
            <main className="pt-28 pb-20 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-cyan-300 mb-6">
                    <ArrowLeft size={14} /> Back
                </Link>

                <div className="grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <span className="section-tag">Book a demo</span>
                        <h1 className="font-display text-4xl sm:text-5xl text-white mt-5 leading-tight tracking-tighter">
                            See WE DELVR LOGISTICS on your own freight.
                        </h1>
                        <p className="text-zinc-400 mt-6 leading-relaxed">
                            30 minutes. We walk through the platform using a lane or shipment
                            relevant to your business. No slides, no sales pitch.
                        </p>
                        <ul className="mt-8 space-y-3 text-sm text-zinc-300">
                            {[
                                "Walk through visibility, audit & advisory features",
                                "We map it to your actual trade lanes",
                                "Q&A with someone who's actually done freight ops",
                            ].map((b) => (
                                <li key={b} className="flex items-start gap-3">
                                    <CheckCircle2 size={16} className="text-cyan-400 mt-0.5" />
                                    {b}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="lg:col-span-7">
                        <div className="card-glow rounded-2xl p-7 lg:p-9">
                            {done ? (
                                <Success label="Demo request received" sub="A team member will reach out within one business day to confirm a time." onReset={() => { setDone(false); setForm(INIT); }} />
                            ) : (
                                <form onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-4" data-testid="demo-form">
                                    <Input id="demo-name" label="Full name *" value={form.name} onChange={set("name")} required />
                                    <Input id="demo-email" type="email" label="Work email *" value={form.email} onChange={set("email")} required />
                                    <Input id="demo-company" label="Company" value={form.company} onChange={set("company")} />
                                    <Input id="demo-role" label="Role" value={form.role} onChange={set("role")} />
                                    <Input id="demo-phone" label="Phone (optional)" value={form.phone} onChange={set("phone")} />
                                    <Input id="demo-date" label="Preferred date" type="date" value={form.preferred_date} onChange={set("preferred_date")} />
                                    <div className="sm:col-span-2">
                                        <Label>What would you like to focus on?</Label>
                                        <textarea
                                            rows={4}
                                            value={form.message}
                                            onChange={set("message")}
                                            className="input-dark w-full rounded-lg px-4 py-3 text-sm resize-none"
                                            placeholder="Lanes, pain points, anything we should know..."
                                            data-testid="demo-input-message"
                                        />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <button type="submit" disabled={busy} className="btn-primary disabled:opacity-60" data-testid="demo-submit">
                                            {busy ? "Sending..." : "Request demo"} <ArrowRight size={16} />
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}

function Input({ id, label, type = "text", value, onChange, required }) {
    return (
        <label className="block" htmlFor={id}>
            <Label>{label}</Label>
            <input
                id={id}
                type={type}
                value={value}
                onChange={onChange}
                required={required}
                className="input-dark w-full rounded-lg px-4 py-3 text-sm"
                data-testid={`input-${id}`}
            />
        </label>
    );
}

function Label({ children }) {
    return (
        <span className="block text-xs font-mono-accent text-zinc-400 mb-2 uppercase tracking-[0.18em]">
            {children}
        </span>
    );
}

function Success({ label, sub, onReset }) {
    return (
        <div className="text-center py-8" data-testid="form-success">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full border border-cyan-400/40 bg-cyan-400/10 mb-5">
                <CheckCircle2 size={28} className="text-cyan-300" />
            </div>
            <h3 className="font-display text-2xl text-white">{label}</h3>
            <p className="text-zinc-400 mt-2 max-w-md mx-auto">{sub}</p>
            <button onClick={onReset} className="btn-secondary mt-7 mx-auto">Submit another</button>
        </div>
    );
}
